/**
 * The use cases as the Gallery and the first run show them: built-ins
 * grouped into families (`supply-abc` and `supply-1key` are one card with two
 * ways to play), each variant labelled by the keys it takes, a search over
 * everything a case says, and the hand-off that sends a play or a search to
 * the Gallery. One copy, so the first run and the Gallery never group or
 * label the same cases differently. Pure, but for the hand-off.
 */

import type { AppContext, UseCase } from '../contracts.ts';

/** `supply-abc` and `supply-1key` are one family; the suffix names how many keys it takes. */
export const familyKey = (id: string): string => id.replace(/-(\d+key|[a-c]{2,3})$/, '');

export const variantLabel = (uc: Pick<UseCase, 'personas'>): string =>
  uc.personas.length <= 1 ? '1-key' : `${uc.personas.length} keys (${uc.personas.join(' ')})`;

export interface Family {
  readonly key: string;
  readonly title: string;
  readonly blurb: string;
  readonly variants: readonly { readonly id: string; readonly label: string; readonly uc: UseCase }[];
}

const shortTitle = (t: string): string => t.replace(/\s*\((?:\d+\s*keys?|1-key|one key)[^)]*\)\s*$/i, '').replace(/\s*[,·–-]\s*(?:\d+\s*keys?|1-key|one key)\s*$/i, '').trim();

export const familiesOf = (cases: readonly UseCase[]): Family[] => {
  const map = new Map<string, UseCase[]>();
  for (const uc of cases) map.set(familyKey(uc.id), [...(map.get(familyKey(uc.id)) ?? []), uc]);
  return [...map].map(([key, ucs]) => {
    const sorted = [...ucs].sort((a, b) => b.personas.length - a.personas.length);
    const first = sorted[0]!;
    return {
      key,
      title: shortTitle(first.title) || first.title,
      blurb: first.blurb,
      variants: sorted.map((uc) => ({ id: uc.id, label: sorted.length > 1 ? variantLabel(uc) : '', uc })),
    };
  });
};

export const searchCases = (cases: readonly UseCase[], q: string): UseCase[] => {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [...cases];
  return cases.filter((uc) => {
    const hay = [uc.title, uc.blurb, uc.id, ...uc.worlds, ...uc.steps.map((s) => s.op), ...uc.steps.map((s) => s.say ?? '')].join(' ').toLowerCase();
    return words.every((w) => hay.includes(w));
  });
};

const COMMON = new Set(['with', 'what', 'that', 'this', 'from', 'into', 'have', 'through', 'three', 'still', 'free', 'tomorrow', 'worlds', 'world', 'make', 'show', 'please', 'them', 'there', 'their', 'about', 'some', 'every', 'each', 'when', 'will', 'your', 'mine']);

/**
 * Cases a free prompt is about, best first: each word of four letters or more (common ones aside) that a case's
 * words contain counts one. "set up a clinic day with a waitlist" is the Clinic; "race me for 10:00" the race.
 */
export const casesFor = (cases: readonly UseCase[], prompt: string, max = 2): UseCase[] => {
  const words = [...new Set(prompt.toLowerCase().split(/[^a-z0-9:]+/).filter((w) => w.length >= 4 && !COMMON.has(w)))];
  if (!words.length) return [];
  const scored = cases.map((uc) => {
    const hay = [uc.title, uc.blurb, uc.id, ...uc.worlds, ...uc.steps.map((s) => s.say ?? '')].join(' ').toLowerCase();
    return { uc, n: words.filter((w) => hay.includes(w)).length };
  }).filter((x) => x.n > 0);
  scored.sort((a, b) => b.n - a.n || (a.uc.origin === 'builtin' ? -1 : 0) - (b.uc.origin === 'builtin' ? -1 : 0));
  // One card per family: the 1-key variant of a case is the same case.
  const seen = new Set<string>();
  return scored.filter((x) => { const k = x.uc.title.replace(/,.*$/, ''); if (seen.has(k)) return false; seen.add(k); return true; }).slice(0, max).map((x) => x.uc);
};

/**
 * Take the tab to the Gallery and ask it to play or search: at once if it is on screen, else the
 * bus keeps the ask until it mounts.
 */
export const requestGallery = (ctx: Pick<AppContext, 'router' | 'bus'>, ask: { play: string } | { search: string }): void => {
  if (ctx.router.current().name !== 'gallery') ctx.router.go({ name: 'gallery' });
  ctx.bus.emit('gallery', ask, { keep: true });
};
